import dayjs from 'dayjs';
import type { CountryCode } from 'libphonenumber-js/max';
import { getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js/max';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import uniqWith from 'lodash/uniqWith';
import { useEffect } from 'react';
import type { CalendarProps } from 'react-calendar';
import type { IntlShape } from 'react-intl';

import { sortAlphabeticallyObjectByKey } from '@/utils/array';

import type { SelectOption } from '@/components/Select';
import {
  getLangSlugForAlgolia,
  getTranslatedStoreValue,
} from '@/components/StoresList/utils';
import { saleorAuthClient } from '@/graphql/client';
import type { UserFragment } from '@/graphql/saleor/api';
import useSetState from '@/hooks/useSetState';
import type { StoreHit } from '@/integrations/algolia/types';
import type { Weekday } from '@/lib/boutique/helpers';
import { getCountryNameFromCode } from '@/lib/boutique/helpers';
import { getUtmFieldsCamelCase } from '@/lib/campaignTracker';
import { BRAND } from '@/lib/const';
import { paths } from '@/lib/paths';
import { messages as boutiqueMessages } from '@/messages/boutique';
import { mrMap, mrsMap, msMap } from '@/views/NewsLetterView/helpers';
import type { BookAppointmentModalState } from '@/views/WatchView/components/BookAppointmentModal/BookAppointmentModal';

export const createSearchOptions = ({
  countryCode,
  localeSlug,
  offersAppointment,
  productType,
  storeId,
  webVisible,
}: {
  countryCode: string;
  localeSlug: string;
  offersAppointment?: boolean;
  productType?: string;
  storeId?: string;
  webVisible?: boolean;
}) => {
  let filters = `country_code:${countryCode.toUpperCase()} AND (type:BOUTIQUE OR type:FLAG_SHIP_BOUTIQUE OR type:OFFICIAL_RETAILER)`;
  if (storeId) filters += ` AND store_id:${storeId}`;
  if (webVisible !== undefined) filters += ` AND web_visible:${webVisible}`;
  if (offersAppointment !== undefined)
    filters += ` AND offers_appointment:${offersAppointment}`;
  if (productType === 'eyewear') {
    filters += ` AND eyewear_available:true`;
  }

  return {
    filters,
    attributesToRetrieve: [
      'region',
      `region_${getLangSlugForAlgolia(localeSlug)}`,
      'temporary_hours',
      'utc_offset',
      'objectID',
      'name',
      `name_${getLangSlugForAlgolia(localeSlug)}`,
      'street',
      `street_${getLangSlugForAlgolia(localeSlug)}`,
      'street2',
      `street2_${getLangSlugForAlgolia(localeSlug)}`,
      'city',
      `city_${getLangSlugForAlgolia(localeSlug)}`,
      'appointments',
      'store_id',
      'ecomm_available',
      'slug',
      'photo_gallery',
      'phone',
      'postal_code',
      `postal_code_${getLangSlugForAlgolia(localeSlug)}`,
      'operating_type',
      'offers_appointment',
      'type',
      'hours',
    ],
    hitsPerPage: 2000,
  };
};

export type RetailerOption = SelectOption<string, string, StoreHit>;
export type RegionCityOption = SelectOption<
  string,
  string,
  {
    imageUrl?: string;
  }
>;

export const getRetailerOptsFromSearchResults = (
  searchResults: StoreHit[],
  localeSlug: string,
): RetailerOption[] => {
  const retailerList = uniqBy(
    searchResults.map((item) => ({
      label: getTranslatedStoreValue(item, 'name', localeSlug),
      value: item.store_id ?? '',
      data: item,
    })),
    'value',
  );
  const sameNameRetailersSet: Set<RetailerOption> = new Set();
  for (const a of retailerList) {
    for (const b of retailerList) {
      if (a.label === b.label && a.value !== b.value) {
        sameNameRetailersSet.add(a);
        sameNameRetailersSet.add(b);
        break;
      }
    }
  }
  const sameNameRetailersArr = Array.from(sameNameRetailersSet);
  const result: RetailerOption[] = retailerList.map((item) => {
    if (
      sameNameRetailersArr.find((retailer) => retailer.label === item.label)
    ) {
      return {
        ...item,
        label: `${item.label} ${getTranslatedStoreValue(
          item.data,
          'street',
          localeSlug,
        )}`,
      };
    }
    return item;
  });
  return result;
};
export const getRegionOptsFromSearchResults = (
  searchResults: StoreHit[],
): RegionCityOption[] => {
  return uniqBy(
    searchResults.map((item) => ({
      label: item?.region ?? '',
      value: item?.region ?? '',
      data: {
        imageUrl: item.photo_gallery?.[0]?.image?.url,
      },
    })),
    'value',
  );
};
export const getOptsFromCountryPage = (
  searchResults: StoreHit[],
  localeSlug: string,
): RetailerOption[] => {
  return uniqBy(
    searchResults.map((item) => ({
      label: getTranslatedStoreValue(item, 'name', localeSlug),
      value: item.store_id ?? '',
      data: item,
    })),
    'value',
  );
};
export const getUserPhone = (user: Partial<UserFragment> | null) =>
  user?.metadata?.find((item) => item.key === 'user.phone')?.value;

export const getUserPhonePrefix = (
  user: Partial<UserFragment> | null,
  countryCode?: string,
) =>
  user?.metadata?.find((item) => item.key === 'user.phonePrefix')?.value ??
  `+${getCountryCallingCode(countryCode as CountryCode)}`;

export const getCountryNameFromPhoneNumber = (phoneNumber: string) => {
  const parsedNumber = parsePhoneNumber(phoneNumber);
  return parsedNumber
    ? getCountryNameFromCode(parsedNumber.country)
    : 'United States';
};

export const getUserPhonePrefixLabel = (
  prefix: string,
  phone?: string,
  countryCode?: string,
) => {
  if (!phone) return `${getCountryNameFromCode(countryCode)}(${prefix})`;
  const countryName = getCountryNameFromPhoneNumber(prefix + phone);
  return `${countryName}(${prefix})`;
};

export const getPersonalTitle = (title: string) => {
  if (Object.values(mrMap)?.includes(title)) {
    return 'Mr';
  } else if (Object.values(mrsMap)?.includes(title)) {
    return 'Mrs';
  } else if (Object.values(msMap)?.includes(title)) {
    return 'Ms';
  } else {
    return '';
  }
};
export const useCalendarMonthTranslation = (
  activeStartMonth: number,
  langCode: string,
  bookAppointmentModalState?: BookAppointmentModalState,
) => {
  useEffect(() => {
    const element = document.querySelector(
      '.react-calendar__navigation__label__labelText',
    );
    const langToDateTimeFormat = langCode.replace('_', '-').toLowerCase();
    if (element) {
      let monthTranslation: string | null = null;
      const year = new Date().getFullYear();
      const date = new Date(year, activeStartMonth - 1, 1);
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const currentMonthName = monthNames[activeStartMonth - 1];
      const monthNameTranslated = new Intl.DateTimeFormat(
        langToDateTimeFormat,
        { month: 'long' },
      ).format(date);
      monthTranslation =
        element.textContent?.replace(
          currentMonthName,
          capitalize(monthNameTranslated),
        ) ?? null;

      if (monthTranslation !== null) {
        element.textContent = monthTranslation;
      }
    }
  }, [activeStartMonth, bookAppointmentModalState]);
};

export const handleCalendarMonthChangeUtil = (
  setMonth: React.Dispatch<React.SetStateAction<number>>,
): CalendarProps['onActiveStartDateChange'] => {
  return ({ activeStartDate }) => {
    if (activeStartDate) {
      const month = activeStartDate.getMonth() + 1;
      setMonth(month);
    }
  };
};

export type AppointmentDataType = {
  brand?: string;
  comments?: string;
  email?: string;
  firstName?: string;
  firstNameKanji?: string;
  languageCode?: string;
  lastName?: string;
  lastNameKanji?: string;
  locationId?: string;
  newsletterOptIn?: boolean;
  phone?: string;
  reason?: string;
  reserveProductSku?: string | null;
  salutation?: string;
  source?: string;
  startDateTime?: string;
  websiteCountryCode?: string;
};

export const createAppointment = async (
  AppointmentData: AppointmentDataType,
) => {
  const utmFields = getUtmFieldsCamelCase(
    AppointmentData.websiteCountryCode ?? '',
  );
  const appointmentDataWithUtm = {
    ...AppointmentData,
    ...utmFields,
  };
  const appointmentRes = await saleorAuthClient.fetchWithAuth(
    paths.api.appointment,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...appointmentDataWithUtm,
        brand: BRAND.toUpperCase(),
      }),
      cache: 'no-store',
    },
    { allowPassingTokenToThirdPartyDomains: true },
  );

  return await appointmentRes.json();
};

export const makeRegionOptions = (
  hits: StoreHit[],
  formatMessage: IntlShape['formatMessage'],
): RegionCityOption[] => {
  const opts = getRegionOptsFromSearchResults(hits);
  const filteredRegionOpts = opts.filter(
    (opt) => opt.label && opt.label.trim() !== '',
  );
  const sortedRegionOpts: RegionCityOption[] = sortAlphabeticallyObjectByKey(
    filteredRegionOpts as [],
    'label',
  );

  if (sortedRegionOpts.length > 0) {
    sortedRegionOpts.unshift({
      label: formatMessage(boutiqueMessages.allRegions),
      value: '',
    });
  }
  return sortedRegionOpts;
};

export type BoutiqueSelectStateType = {
  boutique: RetailerOption | null;
  boutiqueList: StoreHit[];
  boutiqueOptions: RetailerOption[];
  city: RegionCityOption | null;
  cityOptions: RegionCityOption[];
  region: RegionCityOption | null;
  regionOptions: RegionCityOption[];
};

export function makeCityOptions(stores: StoreHit[]): RegionCityOption[] {
  const cityOpts = uniqWith(
    stores.map((item) => ({
      label: item.city,
      value: item.city,
    })),
    isEqual,
  );
  return sortAlphabeticallyObjectByKey(cityOpts as [], 'label');
}

export function getNextAvailableDate(
  start: Date,
  unavailable?: Weekday[],
  maxDays = 29, // Because the available reservations are for the last 30 days, and will start from tomorrow
): string {
  let candidate = dayjs(start);

  for (let i = 0; i < maxDays; i++) {
    const weekday = candidate.format('dddd').toLowerCase() as Weekday;

    if (!unavailable?.includes(weekday)) {
      return candidate.format('YYYY-MM-DD');
    }
    candidate = candidate.add(1, 'day'); // move to next day
  }

  return '';
}

export const resolveUnavailableAppointmentWeekdays = (
  store?: StoreHit,
): Weekday[] => {
  if (!store) return [];

  const closed = Object.entries(store.hours ?? {})
    .filter(([, { isClosed }]) => isClosed)
    .map(([day]) => day as Weekday);

  const noSlots = Object.entries(store.appointments ?? {})
    .filter(([, slots]) => slots.length === 0)
    .map(([day]) => day as Weekday);

  return Array.from(new Set([...closed, ...noSlots]));
};

export const useBoutiqueSelect = (localeSlug: string) => {
  const [boutiqueSelectState, setBoutiqueSelectState] =
    useSetState<BoutiqueSelectStateType>({
      region: null,
      city: null,
      regionOptions: [],
      cityOptions: [],
      boutiqueList: [],
      boutiqueOptions: [],
      boutique: null,
    });

  const handleChangeRegion = (opt: RegionCityOption) => {
    setBoutiqueSelectState({
      region: opt,
      city: null,
      boutique: null,
    });

    const stores = !opt.value
      ? boutiqueSelectState.boutiqueList
      : boutiqueSelectState.boutiqueList.filter(
          (item) => item.region === opt.value,
        );
    const sortedCityOpts = makeCityOptions(stores);
    const retailerOpts = getRetailerOptsFromSearchResults(stores, localeSlug);
    setBoutiqueSelectState({
      cityOptions: sortedCityOpts,
      boutiqueOptions: retailerOpts,
    });
  };

  const handleChangeCity = (opt: RegionCityOption) => {
    setBoutiqueSelectState({
      city: opt,
      boutique: null,
    });

    // Start from region-filtered list if region is selected; otherwise full list
    const baseStores = boutiqueSelectState.region?.value
      ? boutiqueSelectState.boutiqueList.filter(
          (item) => item.region === boutiqueSelectState.region?.value,
        )
      : boutiqueSelectState.boutiqueList;

    const stores = opt?.value
      ? baseStores.filter((item) => item.city === opt.value)
      : baseStores;

    const retailerOpts = getRetailerOptsFromSearchResults(stores, localeSlug);

    setBoutiqueSelectState({
      boutiqueOptions: retailerOpts,
    });
  };

  const handleChangeBoutique = (opt: RetailerOption) => {
    setBoutiqueSelectState({
      boutique: opt,
    });
  };

  return {
    handleChangeRegion,
    handleChangeCity,
    handleChangeBoutique,
    boutiqueSelectState,
    setBoutiqueSelectState,
  };
};
